import React, { useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, UserPlus } from 'lucide-react';
import { DemoStage } from './DemoChrome';
import { gentleCrossfade } from './motionPresets';
import { useDemoAutoplay } from './useDemoAutoplay';
import './marketingDemos.css';

const ROLES = [
  {
    id: 'owner',
    label: 'Owner',
    desc: 'Billing, delete workspace, transfer ownership',
    permissions: ['Manage billing', 'Delete workspace', 'Transfer ownership'],
  },
  {
    id: 'manager',
    label: 'Manager',
    desc: 'Invite teammates and run day-to-day operations',
    permissions: ['Invite members', 'Approve internally', 'Manage pipeline'],
  },
  {
    id: 'editor',
    label: 'Video Editor',
    desc: 'Upload and prep library media',
    permissions: ['Upload files', 'Add review notes', 'Cannot schedule publish'],
  },
  {
    id: 'reviewer',
    label: 'Reviewer',
    desc: 'Review cuts and approve or request changes',
    permissions: ['Add timestamped notes', 'Approve versions', 'Request changes'],
  },
  {
    id: 'producer',
    label: 'Content Producer',
    desc: 'Build drafts and hand off to clients',
    permissions: ['Create drafts', 'Schedule posts', 'Share for client review'],
  },
  {
    id: 'client',
    label: 'Client',
    desc: 'Client reviewer — scoped to review-ready and shared work',
    permissions: [
      'Approve cuts or request changes',
      'Only sees files ready for client review or explicitly shared with them',
      'No internal notes, billing, or workspace settings',
    ],
  },
];

export default function TeamRolesDemo() {
  const rootRef = useRef(null);
  const [roleIndex, setRoleIndex] = useState(0);
  const [invited, setInvited] = useState(false);

  const role = ROLES[roleIndex];

  const onAutoplayTick = useCallback(() => {
    if (invited) {
      setInvited(false);
      setRoleIndex(0);
      return;
    }

    setRoleIndex((i) => {
      if (i >= ROLES.length - 1) {
        setInvited(true);
        return i;
      }
      return i + 1;
    });
  }, [invited]);

  useDemoAutoplay(rootRef, onAutoplayTick, invited ? 1300 : 1050);

  return (
    <div ref={rootRef}>
      <DemoStage visualClassName="mkt-demo-visual--team">
        <div className="mkt-invite-panel">
          <div className="mkt-invite-panel-header">
            <UserPlus className="ui-icon-xl" aria-hidden />
            <span>Invite to workspace</span>
          </div>

          <div className="mkt-invite-field">
            <span className="mkt-invite-field-label">Email</span>
            <div className="mkt-invite-input-wrap mkt-invite-input-wrap--auto">
              <Mail className="ui-icon-md" aria-hidden />
              <span className="mkt-invite-input-static">alex@agency.com</span>
            </div>
          </div>

          <div className="mkt-invite-field">
            <span className="mkt-invite-field-label">Role</span>
            <div className="mkt-invite-role-grid">
              {ROLES.map((r, index) => (
                <motion.div
                  key={r.id}
                  className={`mkt-invite-role-option mkt-invite-role-option--auto${r.id === 'client' ? ' mkt-invite-role-option--client' : ''}${roleIndex === index ? ' mkt-invite-role-option--active' : ''}`}
                >
                  {r.label}
                </motion.div>
              ))}
            </div>
          </div>

          <div className="mkt-demo-crossfade-stage mkt-invite-permissions-stage">
            <AnimatePresence mode="sync" initial={false}>
              <motion.div
                key={role.id}
                className="mkt-invite-role-detail"
                variants={gentleCrossfade}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <ul className="mkt-invite-permissions">
                  {role.permissions.map((perm) => (
                    <li key={perm}>{perm}</li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>

          <div
            className={`mkt-invite-submit mkt-invite-submit--auto${invited ? ' mkt-invite-submit--sent' : ''}`}
          >
            {invited ? 'Invite sent!' : 'Send invite'}
          </div>
        </div>
      </DemoStage>
    </div>
  );
}
